import React, { useState, useEffect } from 'react';
import { enquiriesApi, roomTypesApi } from '../../services/api';
import { fmt, getTodayStr } from '../../utils/billing';
import { useAuth } from '../../context/AuthContext';
import { useNotify } from '../../context/ConfirmContext';

export default function EnquiryPage({ hotel, onSuccess }) {
  const notify = useNotify();
  const { user } = useAuth();
  const [enquiries, setEnquiries] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    guestName: '', phone: '', roomType: 'Standard', offeredPrice: '', checkInDate: '', followUpDate: '', status: 'pending', notes: ''
  });
  const [roomTypes, setRoomTypes] = useState([]);

  // eslint-disable-next-line react-hooks/exhaustive-deps -- load is redefined every render
  useEffect(() => { load(); }, [hotel]);

  async function load() {
    if (!hotel) return;
    try {
      const [all, types] = await Promise.all([
        enquiriesApi.list(hotel.id),
        roomTypesApi.list(hotel.id)
      ]);
      const mapped = all.map(q => ({
        ...q,
        guestName: q.guest_name,
        phone: q.guest_phone,
        roomType: q.room_type,
        offeredPrice: q.offered_price,
        checkInDate: q.check_in,
        followUpDate: q.follow_up_date,
        createdAt: q.created_at
      }));
      setEnquiries(mapped.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
      setRoomTypes(types);
    } catch (err) { console.error('Load Error:', err); }
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await enquiriesApi.create({
        ...form,
        hotelId: hotel.id,
        createdBy: user?.name
      });
      setShowForm(false);
      onSuccess?.('Enquiry logged successfully.');
      load();
      setForm({ guestName: '', phone: '', roomType: 'Standard', offeredPrice: '', checkInDate: '', followUpDate: '', status: 'pending', notes: '' });
    } catch (err) { await notify(err.message); }
  };

  const updateStatus = async (id, status) => {
    try {
      await enquiriesApi.update(id, { status });
      load();
    } catch (err) { await notify(err.message); }
  };

  return (
    <div>
      <div className="panel">
        <div className="panel-head">
          <span className="panel-title">Potential Leads & Follow-ups</span>
          <button className="btn btn-gold btn-sm" onClick={() => setShowForm(true)}>+ New Enquiry</button>
        </div>
        <div className="tbl-wrap">
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Guest Name</th>
                <th>Phone</th>
                <th>Required</th>
                <th>Offered Price</th>
                <th>Expected Stay</th>
                <th>Follow-up</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {enquiries.length === 0 ? (
                <tr><td colSpan={8}><div className="empty">No leads found. Start by adding one!</div></td></tr>
              ) : enquiries.map(q => (
                <tr key={q.id}>
                  <td>{new Date(q.createdAt).toLocaleDateString()}</td>
                  <td style={{ fontWeight: 600 }}>{q.guestName}</td>
                  <td>{q.phone}</td>
                  <td><span className="badge b-s1">{q.roomType}</span></td>
                  <td style={{ fontWeight: 700, color: 'var(--teal)' }}>{fmt(q.offeredPrice)}</td>
                  <td>{q.checkInDate || '—'}</td>
                  <td style={{ color: q.followUpDate < getTodayStr() ? 'var(--danger)' : 'inherit', fontWeight: q.followUpDate === getTodayStr() ? 'bold' : 'normal' }}>
                    {q.followUpDate || '—'}
                  </td>
                  <td>
                    <select value={q.status} onChange={e => updateStatus(q.id, e.target.value)} style={{ padding: '4px 8px', borderRadius: 4, fontSize: 11 }}>
                      <option value="pending">⏳ Pending</option>
                      <option value="called">📞 Called</option>
                      <option value="booked">✅ Booked</option>
                      <option value="lost">❌ Lost</option>
                    </select>
                  </td>
                  <td>
                    <button className="btn btn-ghost btn-sm" onClick={() => notify({ icon: '📝', title: 'Notes', body: q.notes || 'No notes.' })}>👁️ Notes</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <div className="overlay" onClick={() => setShowForm(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-title">Log New Enquiry</div>
            <form onSubmit={handleSubmit}>
              <div className="form-row-2">
                <div className="field"><label>Guest Name</label><input required value={form.guestName} onChange={e => setForm(f => ({ ...f, guestName: e.target.value }))} /></div>
                <div className="field"><label>Phone Number</label><input required value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} /></div>
              </div>
              <div className="form-row-2" style={{ marginTop: 14 }}>
                <div className="field">
                  <label>Room Type</label>
                  <select value={form.roomType} onChange={e => setForm(f => ({ ...f, roomType: e.target.value }))}>
                    {roomTypes.map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
                  </select>
                </div>
                <div className="field"><label>Offered Price (₹)</label><input type="number" required value={form.offeredPrice} onChange={e => setForm(f => ({ ...f, offeredPrice: e.target.value }))} /></div>
              </div>
              <div className="form-row-2" style={{ marginTop: 14 }}>
                <div className="field">
                  <label>Expected Check-in</label>
                  <input type="date" min={getTodayStr()} value={form.checkInDate} onChange={e => setForm(f => ({ ...f, checkInDate: e.target.value }))} />
                </div>
                <div className="field">
                  <label>Next Follow-up Date</label>
                  <input type="date" min={getTodayStr()} value={form.followUpDate} onChange={e => setForm(f => ({ ...f, followUpDate: e.target.value }))} />
                </div>
              </div>
              <div className="field" style={{ marginTop: 14 }}>
                <label>Requirements / Notes</label>
                <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Any specific requests?" />
              </div>
              <div className="modal-foot" style={{ marginTop: 25 }}>
                <button type="button" className="btn btn-ghost" onClick={() => setShowForm(false)}>Cancel</button>
                <button type="submit" className="btn btn-teal">Save Enquiry</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
